import { css, styled } from "@mui/material";
import MuiButton from "@mui/material/Button";

interface StyledButtonProps {
  buttonType?: "primary" | "secondary" | "warning";
  buttonVariant?: "full" | "ghost" | "text";
}

export const StyledButton = styled(MuiButton, {
  shouldForwardProp: (prop) =>
    prop !== "buttonVariant" && prop !== "buttonType",
})<StyledButtonProps>`
  text-transform: initial;
  white-space: nowrap;
  min-width: 81px;
  height: 32px;
  padding: ${({ theme }) => `${theme.wsc.spacing[1]} ${theme.wsc.spacing[2]}`};
  border: 1px solid transparent;
  ${({ theme }) => theme.wsc.typography.button};

  && {
    border-radius: ${({ theme }) => theme.wsc.radius.m};

    &:focus {
      outline: 0;
    }
  }

  .MuiButton-startIcon {
    margin-right: ${({ theme }) => theme.wsc.spacing[0]};
  }

  .MuiButton-endIcon {
    margin-left: ${({ theme }) => theme.wsc.spacing[0]};
  }

  ${({
    buttonType,
    buttonVariant,
    theme: {
      wsc: { palette },
    },
  }) => {
    switch (buttonType) {
      case "primary":
        return css`
          ${buttonVariant === "full" &&
          `
                        && {
                            background-color: ${palette.primary[300]};
                            color: ${palette.primary.contrastText};
                            &:hover {
                                background-color: ${palette.primary[100]};
                            }
                            &:disabled {
                                background-color: ${palette.primary[700]};
                                color: ${palette.neutral[300]};
                            }
                            &:active {
                                background-color: ${palette.primary[500]};
                            }   
                        }
                    `}
          ${buttonVariant === "ghost" &&
          `
                        && {
                            background-color: transparent;
                            color: ${palette.primary[300]};
                            border-color: ${palette.primary[300]};
                            &:hover {
                                background-color: ${palette.neutral[600]};
                                color: ${palette.primary[100]};
                                border-color: ${palette.primary[100]}; 
                            }
                            &:disabled {
                                color: ${palette.primary[700]};
                                border-color: ${palette.primary[700]};
                            }
                            &:active {
                                background-color: ${palette.neutral[500]};
                                border-color: ${palette.primary[500]};
                                color: ${palette.primary[500]};
                            } 
                        }
                    `}
          ${buttonVariant === "text" &&
          `
                        && {
                            background-color: transparent;
                            color: ${palette.primary[300]};
                            &:hover {
                                background-color: ${palette.neutral[600]};
                                color: ${palette.primary[100]};
                            }
                            &:disabled {
                                color: ${palette.primary[700]};
                            }
                            &:active {
                                background-color: ${palette.neutral[500]};
                                color: ${palette.primary[500]};
                            } 
                        }
                    `}
        `;
      case "secondary":
        return css`
          ${buttonVariant === "full" &&
          `
                        && {
                            background-color: ${palette.neutral[700]};
                            color: ${palette.neutral[100]};
                            &:hover {
                                background-color: ${palette.neutral[600]};
                            }
                            &:disabled {
                                background-color: transparent;
                                color: ${palette.neutral[400]};
                                border-color: ${palette.neutral[400]};
                            }
                            &:active {
                                background-color: ${palette.neutral[500]};
                                color: ${palette.primary[500]};
                            }  
                        } 
                    `}
          ${buttonVariant === "ghost" &&
          `
                        && {
                            background-color: transparent;
                            color: ${palette.neutral[100]};
                            border-color: ${palette.neutral[100]};
                            &:hover {
                                background-color: ${palette.neutral[600]};
                            }
                            &:disabled {
                                color: ${palette.neutral[400]};
                                border-color: ${palette.neutral[400]};
                                pointer-events: none;
                            }
                            &:active {
                                background-color: ${palette.neutral[500]};
                                color: ${palette.primary[500]};
                                border-color: ${palette.primary[500]};
                            }
                        }
                    `}
          ${buttonVariant === "text" &&
          `
                        && {
                            background-color: transparent;
                            color: ${palette.neutral[100]};
                            &:hover {
                                background-color: ${palette.neutral[600]};
                            }
                            &:disabled {
                                color: ${palette.neutral[400]};
                            }
                            &:active {
                                background-color: ${palette.neutral[500]};
                                color: ${palette.primary[500]};
                            }
                        }
                    `}
        `;
      case "warning":
        return css`
          ${buttonVariant === "full" &&
          `
              && {
                  background-color: ${palette.warning[300]};
                  color: ${palette.neutral[100]};
                  &:hover {
                    background-color: ${palette.warning[100]};
                  }
                  &:active {
                    background-color: ${palette.warning[500]};
                  }
                  &:disabled {
                    background-color: ${palette.warning[700]};
                    color: ${palette.neutral[300]};
                  }
              }
          `}

          ${buttonVariant === "ghost" &&
          `
              && {
                  background-color: transparent;
                  color: ${palette.warning[300]};
                  border-color: ${palette.warning[300]};

                  &:hover {
                    background-color: ${palette.neutral[600]};
                    color: ${palette.warning[100]};
                    border-color: ${palette.warning[100]};
                  }
                  
                  &:disabled {
                    color: ${palette.warning[700]};
                    border-color: ${palette.warning[700]};
                  }
                  
                  &:active {
                    background-color: ${palette.neutral[500]};
                    border-color: ${palette.warning[500]};
                    color: ${palette.warning[500]};
                  }
              }
          `}

          ${buttonVariant === "text" &&
          `
              && {
                  background-color: transparent;
                  color: ${palette.warning[300]};

                  &:hover {
                    background-color: ${palette.neutral[600]};
                    color: ${palette.warning[100]};
                  }

                  &:disabled {
                    color: ${palette.warning[700]};
                  }

                  &:active {
                    background-color: ${palette.neutral[500]};
                    color: ${palette.warning[500]};
                  }
              } 
          `}
        `;
    }
  }}
`;

export const StyledLoaderContainer = styled("span")`
  margin-right: ${(props) => props.theme.wsc.spacing[1]};
  margin-top: ${(props) => props.theme.wsc.spacing[0]};
`;
